import { injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'

export type ConfirmOptions = {
  confirmLabel?: string
  cancelLabel?: string
}

export type ActiveDialog = {
  message: string
  /**
   * null means "no caller-supplied override" — DialogPresenter substitutes the
   * translated default. Copy does not live in a Repository (§4.4), and a
   * literal here would also freeze the label at the locale that happened to be
   * active when confirm() was called.
   */
  confirmLabel: string | null
  cancelLabel: string | null
}

/**
 * One dialog at a time, not a stack — a second confirm() while one is
 * already open is almost always a bug (stacking modal confirms is bad UX
 * regardless), so it's simpler to just not support it until something
 * actually needs it.
 *
 * The pending `resolve` is state, so it lives here with the dialog it belongs
 * to: `confirm()` returns a Promise settled by a later, separate user event
 * (draft §I), and both halves have to be owned by the same object.
 */
@injectable()
export class DialogRepository implements UserScopedState {
  dialogPm: ActiveDialog | null = null

  private pendingResolve: ((value: boolean) => void) | null = null

  constructor() {
    makeObservable(this, {
      dialogPm: observable,
      confirm: action,
      resolve: action,
    })
  }

  confirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
    // A second confirm() while one is open used to overwrite pendingResolve and
    // silently drop it, leaving the first caller awaiting a promise that could
    // never settle — a hung transaction with no error anywhere. Whatever the
    // first question was, the user is no longer being shown it, so the honest
    // answer is "no".
    this.pendingResolve?.(false)

    return new Promise((resolve) => {
      this.pendingResolve = resolve
      this.dialogPm = {
        message,
        confirmLabel: options.confirmLabel ?? null,
        cancelLabel: options.cancelLabel ?? null,
      }
    })
  }

  resolve(value: boolean) {
    this.pendingResolve?.(value)
    this.pendingResolve = null
    this.dialogPm = null
  }

  /**
   * A dialog left open across logout would otherwise show the next user the
   * previous user's question, and its pending Promise (e.g. Router.goToId's
   * leave-confirm, core/routing/router.ts) would resolve into the new
   * session whenever someone eventually clicks it. Same "no longer on
   * screen, honest answer is no" reasoning as the second-confirm() branch
   * above — resolve(false) both clears dialogPm and settles the Promise.
   */
  resetUserScopedState(): void {
    this.resolve(false)
  }
}
